import { defineMessages, FormattedMessage, type IntlShape } from 'react-intl';
import Stats from '@alltrails/core/types/Stats';
import { metersToKilometers, metersToMiles } from '@alltrails/data-formatting/distanceConverters';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useAppSelector } from '../../../utils/redux';
import CommunityStat from './CommunityStat';
import styles from './styles.module.scss';

const COMMUNTIY_STATS_STRINGS = defineMessages({
  CURATED_TRAILS: {
    defaultMessage: 'curated trails'
  },
  DISCOVER_UNEXPECTED_GEMS: {
    defaultMessage: 'Discover unexpected gems, even in your own backyard.'
  },
  FELLOW_EXPLORERS: {
    defaultMessage: 'fellow explorers'
  },
  SHARE_YOUR_ADVENTURES: {
    defaultMessage: 'Share your adventures and learn from our global community.'
  },
  LOGGED_MILES: {
    defaultMessage: 'logged miles'
  },
  LOGGED_KILOMETERS: { defaultMessage: 'logged kilometers' },
  NAVIGATE_YOUR_WAY: { defaultMessage: 'Navigate your way and keep a record of all your travels.' }
});

const ONE_BILLION = 1000000000;

// Helper function to format the logged distance in miles or kilometers for alltrails landing pages

const formatLoggedDistance = (meters: number, intl: IntlShape, displayMetric = false) => {
  const distance = displayMetric ? metersToKilometers(meters) : metersToMiles(meters);

  return intl.formatNumber(Math.round(distance) / ONE_BILLION, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
};

/**
 * CommunityStats displays community statistics including curated trails,
 * fellow explorers, and logged distance using values from the Stats module
 */
const CommunityStats = () => {
  const {
    intl,
    formattedDefaultMessages: {
      LOGGED_KILOMETERS,
      LOGGED_MILES,
      CURATED_TRAILS,
      DISCOVER_UNEXPECTED_GEMS,
      FELLOW_EXPLORERS,
      SHARE_YOUR_ADVENTURES,
      NAVIGATE_YOUR_WAY
    }
  } = useFormatMessage(COMMUNTIY_STATS_STRINGS);
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const totalDistance = formatLoggedDistance(Stats.LoggedDistanceMeters, intl, displayMetric);
  const loggedDistanceMeasurement = displayMetric ? LOGGED_KILOMETERS : LOGGED_MILES;

  return (
    <div className={styles.container}>
      <CommunityStat
        title={<FormattedMessage defaultMessage="{curatedTrails}K+" values={{ curatedTrails: Stats.NumCuratedTrails }} />}
        subtitle={CURATED_TRAILS}
        text={DISCOVER_UNEXPECTED_GEMS}
      />
      <CommunityStat
        title={<FormattedMessage defaultMessage="{fellowExplorers}M+" values={{ fellowExplorers: Stats.NumUsers }} />}
        subtitle={FELLOW_EXPLORERS}
        text={SHARE_YOUR_ADVENTURES}
      />
      <CommunityStat
        title={<FormattedMessage defaultMessage="{totalDistance}B+" values={{ totalDistance }} />}
        subtitle={loggedDistanceMeasurement}
        text={NAVIGATE_YOUR_WAY}
      />
    </div>
  );
};

export default CommunityStats;
